import React, { useMemo, useState } from 'react';
import { CalendarClock, Plus, X } from 'lucide-react';
import {
  scheduleDeliverableChange,
  cancelScheduledDeliverableChange,
  describeScheduledChange,
} from '../../services/scheduledDeliverableChangesService';
import {
  getAccountStrategyCallPeriod,
  formatLocalDateKey,
  parseFlexibleAccountDate,
} from '../../utils/accountProgramDeliverables';

const ACTION_BADGE_STYLES = {
  scheduled: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300',
  applied: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
  'schedule-cancelled': 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
  added: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
  removed: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
  'quantity-changed': 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
  'added-period-only': 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
  'removed-period-only': 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
};

const ACTION_LABELS = {
  scheduled: 'Scheduled',
  applied: 'Applied',
  'schedule-cancelled': 'Cancelled',
  added: 'Added',
  removed: 'Removed',
  'quantity-changed': 'Quantity',
  'added-period-only': 'One-period add',
  'removed-period-only': 'One-period remove',
};

/**
 * "Next Period" modal for the ClientOverview Program tab: stage deliverable changes that apply on
 * the reset date (without touching the current period), cancel pending ones, and browse the full
 * deliverable change log.
 */
export default function NextPeriodDeliverablesModal({
  clientId,
  account,
  contractData,
  programItems,
  availableItems,
  isEditMode,
  actorName,
  onClose,
  onAccountFieldsUpdated,
}) {
  const [activeTab, setActiveTab] = useState('next');
  const [selectedItemId, setSelectedItemId] = useState('');
  const [action, setAction] = useState('add');
  const [qtyValue, setQtyValue] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const billingPeriod = useMemo(
    () => getAccountStrategyCallPeriod(account, contractData),
    [account, contractData]
  );
  const resetDate = billingPeriod?.periodEnd || null;

  const pendingChanges = useMemo(() => {
    const list = Array.isArray(account?.scheduledDeliverableChanges)
      ? account.scheduledDeliverableChanges
      : [];
    return [...list].sort((a, b) => String(a?.createdAt || '').localeCompare(String(b?.createdAt || '')));
  }, [account?.scheduledDeliverableChanges]);

  const changeLog = useMemo(() => {
    const list = Array.isArray(account?.deliverableChangeLog) ? account.deliverableChangeLog : [];
    return [...list].reverse();
  }, [account?.deliverableChangeLog]);

  // The recurring roster: rows that will still exist next period. One-off (this-period-only) rows
  // are excluded — they vanish at reset, so for "next period" purposes their item is a fresh add.
  const rosterItems = useMemo(
    () => (programItems || []).filter((item) => !item.oneTimePeriodOnly),
    [programItems]
  );
  const rosterIds = useMemo(() => new Set(rosterItems.map((i) => i.id)), [rosterItems]);

  const addableItems = useMemo(
    () =>
      (availableItems || [])
        .filter((i) => i.hasDeliverable && !rosterIds.has(i.id))
        .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))),
    [availableItems, rosterIds]
  );

  const selectedRosterItem = rosterItems.find((i) => i.id === selectedItemId) || null;
  const selectedCatalogItem = (availableItems || []).find((i) => i.id === selectedItemId) || null;
  const selectedName =
    selectedRosterItem?.name ||
    selectedCatalogItem?.name ||
    selectedCatalogItem?.itemName ||
    selectedCatalogItem?.title ||
    '';
  const selectedCurrentQty = selectedRosterItem
    ? Math.max(
        0,
        (Number(selectedRosterItem.programQuantity) || 0) -
          (Number(selectedRosterItem.oneTimePeriodQuantity) || 0)
      )
    : null;

  const handleSelectItem = (itemId) => {
    setSelectedItemId(itemId);
    setError('');
    const rosterItem = rosterItems.find((i) => i.id === itemId);
    if (rosterItem) {
      setAction('quantity');
      setQtyValue(
        Math.max(
          0,
          (Number(rosterItem.programQuantity) || 0) - (Number(rosterItem.oneTimePeriodQuantity) || 0)
        ) || 1
      );
    } else {
      setAction('add');
      setQtyValue(1);
    }
  };

  const handleSchedule = async () => {
    if (!selectedItemId || !resetDate) return;
    setSaving(true);
    setError('');
    try {
      const result = await scheduleDeliverableChange(clientId, {
        type: action,
        itemId: selectedItemId,
        itemName: selectedName || selectedItemId,
        quantity: action === 'remove' ? null : Math.max(action === 'add' ? 1 : 0, parseInt(String(qtyValue), 10) || 0),
        previousQuantity: selectedCurrentQty,
        effectiveDate: resetDate.toISOString(),
        effectivePeriodKey: formatLocalDateKey(resetDate),
        actor: actorName,
      });
      onAccountFieldsUpdated(result);
      setSelectedItemId('');
      setAction('add');
      setQtyValue(1);
    } catch (err) {
      console.error('Error scheduling deliverable change:', err);
      setError('Failed to schedule the change. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelPending = async (changeId) => {
    setSaving(true);
    setError('');
    try {
      const result = await cancelScheduledDeliverableChange(clientId, changeId, actorName);
      onAccountFieldsUpdated(result);
    } catch (err) {
      console.error('Error cancelling scheduled change:', err);
      setError('Failed to cancel the scheduled change. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex-1"></div>
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center">
              <CalendarClock className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Next Period Changes</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {resetDate
                  ? `Apply on reset date: ${resetDate.toLocaleDateString()}`
                  : 'No reset date available'}
              </p>
            </div>
          </div>
          <div className="flex-1 flex justify-end">
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300">
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700 mb-4">
          <nav className="-mb-px flex justify-center space-x-8">
            {[
              { key: 'next', label: 'Next Period' },
              { key: 'log', label: 'Change Log' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.key
                    ? 'border-red-500 text-red-600 dark:text-red-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="overflow-y-auto flex-1">
          {activeTab === 'next' ? (
            <div className="space-y-5">
              {/* Pending scheduled changes */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 text-center">
                  Scheduled changes
                </h4>
                {pendingChanges.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center bg-gray-50 dark:bg-gray-700 rounded-lg py-4">
                    No changes scheduled for the next period.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {pendingChanges.map((change) => (
                      <div
                        key={change.id}
                        className="flex items-center justify-between gap-3 rounded-lg border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/20 px-3 py-2"
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {describeScheduledChange(change)}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {change.createdBy || 'Unknown'}
                            {change.createdAt
                              ? ` · ${parseFlexibleAccountDate(change.createdAt)?.toLocaleDateString() || ''}`
                              : ''}
                            {change.effectiveDate
                              ? ` · applies ${parseFlexibleAccountDate(change.effectiveDate)?.toLocaleDateString() || ''}`
                              : ''}
                          </div>
                        </div>
                        {isEditMode && (
                          <button
                            type="button"
                            disabled={saving}
                            onClick={() => handleCancelPending(change.id)}
                            className="shrink-0 text-xs font-medium text-red-600 dark:text-red-400 hover:underline disabled:opacity-50"
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Schedule a new change */}
              {isEditMode ? (
                <div className="rounded-lg border border-gray-200 dark:border-gray-600 p-4">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 text-center">
                    Schedule a change
                  </h4>
                  <div className="space-y-3">
                    <select
                      value={selectedItemId}
                      onChange={(e) => handleSelectItem(e.target.value)}
                      className="w-full text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-2 py-1.5"
                    >
                      <option value="">Select a deliverable…</option>
                      {rosterItems.length > 0 && (
                        <optgroup label="Current deliverables">
                          {rosterItems.map((i) => (
                            <option key={i.id} value={i.id}>
                              {i.name}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {addableItems.length > 0 && (
                        <optgroup label="Add from catalog">
                          {addableItems.map((i) => (
                            <option key={i.id} value={i.id}>
                              {i.name || i.itemName || i.title || i.id}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </select>

                    {selectedItemId && (
                      <div className="flex flex-wrap items-center justify-center gap-3">
                        {selectedRosterItem ? (
                          <div className="flex items-center gap-3">
                            <label className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                              <input
                                type="radio"
                                name="next-period-action"
                                checked={action === 'quantity'}
                                onChange={() => setAction('quantity')}
                              />
                              Set quantity
                            </label>
                            <label className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                              <input
                                type="radio"
                                name="next-period-action"
                                checked={action === 'remove'}
                                onChange={() => setAction('remove')}
                              />
                              Remove
                            </label>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-700 dark:text-gray-300">Add with quantity</span>
                        )}
                        {action !== 'remove' && (
                          <input
                            type="number"
                            min={action === 'add' ? 1 : 0}
                            value={qtyValue}
                            onChange={(e) => setQtyValue(e.target.value)}
                            className="w-16 text-center text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white py-1"
                          />
                        )}
                        {selectedRosterItem && action === 'quantity' && selectedCurrentQty != null && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            currently {selectedCurrentQty}
                          </span>
                        )}
                      </div>
                    )}

                    {error && (
                      <p className="text-sm text-red-600 dark:text-red-400 text-center">{error}</p>
                    )}

                    <div className="flex justify-center">
                      <button
                        type="button"
                        disabled={!selectedItemId || !resetDate || saving}
                        onClick={handleSchedule}
                        className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                      >
                        <Plus className="w-4 h-4" />
                        {saving ? 'Scheduling…' : 'Schedule for next period'}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                      The current period is not affected. The change applies automatically on the
                      reset date{resetDate ? ` (${resetDate.toLocaleDateString()})` : ''}.
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                  Enter edit mode to schedule or cancel changes.
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {changeLog.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center bg-gray-50 dark:bg-gray-700 rounded-lg py-4">
                  No deliverable changes logged yet.
                </p>
              ) : (
                changeLog.map((entry) => (
                  <div
                    key={entry.id}
                    className="rounded-lg border border-gray-200 dark:border-gray-600 px-3 py-2"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${
                          ACTION_BADGE_STYLES[entry.action] ||
                          'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                        }`}
                      >
                        {ACTION_LABELS[entry.action] || entry.action}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {parseFlexibleAccountDate(entry.timestamp)?.toLocaleString() || '—'}
                      </span>
                    </div>
                    <div className="text-sm text-gray-900 dark:text-white mt-1">{entry.description}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{entry.actor}</div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
